/**
 * Predictive Search (Section Rendering API)
 */
class PredictiveSearch extends HTMLElement {
  static RESULT_LIMIT = 10;

  constructor() {
    super();

    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('[data-predictive-search]');
    this.resultsContainer = this.querySelector('[data-predictive-search-results]');
    this.cachedResults = new Map();
    this.abortController = null;
    this.isOpen = false;

    if (!this.input || !this.results || !this.resultsContainer || !window.routes?.predictive_searh) return;

    this.onChange = Utility.debounce(() => {
      this.onChangeHandler();
    }, 500);

    this.input.setAttribute('autocomplete', 'off');
    this.input.setAttribute('aria-autocomplete', 'list');
    this.input.setAttribute('aria-expanded', 'false');
    this.input.setAttribute('aria-controls', this.results.id || 'predictive-search-panel');
    if (!this.results.id) this.results.id = 'predictive-search-panel';

    this.input.addEventListener('input', this.onChange.bind(this));
    this.input.addEventListener('focus', this.onFocusHandler.bind(this));
  }

  onChangeHandler() {
    const searchTerm = this.input.value.trim();

    if (!searchTerm.length) {
      this.hideResults();
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFocusHandler() {
    const searchTerm = this.input.value.trim();

    if (!searchTerm.length) return;

    if (this.resultsContainer.innerHTML.trim() !== '') {
      this.showResults();
      return;
    }

    this.getSearchResults(searchTerm);
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(/\s/g, '-').toLowerCase();

    if (this.cachedResults.has(queryKey)) {
      this.renderResults(this.cachedResults.get(queryKey));
      return;
    }

    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();

    const typeInput = this.querySelector('form [name="type"]');
    const searchType = typeInput?.value || 'product';
    const limit = PredictiveSearch.RESULT_LIMIT;
    const url = `${window.routes.predictive_searh}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search&resources[type]=${encodeURIComponent(searchType)}&resources[limit]=${limit}&resources[options][fields]=title,vendor,variants.sku,product_type,variants.title`;

    this.setLoadingState(true);

    fetch(url, { signal: this.abortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search')?.innerHTML;

        this.cachedResults.set(queryKey, resultsMarkup || '');
        this.renderResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;
        this.hideResults();
      })
      .finally(() => {
        this.setLoadingState(false);
        this.abortController = null;
      });
  }

  renderResults(resultsMarkup) {
    if (!resultsMarkup) {
      this.hideResults();
      return;
    }

    this.resultsContainer.innerHTML = resultsMarkup;
    this.showResults();
  }

  showResults() {
    if (this.isOpen) return;

    this.isOpen = true;
    this.results.classList.add('show-results');
    siteOverlay.prototype.showOverlay();
    this.input.setAttribute('aria-expanded', 'true');
  }

  hideResults() {
    if (!this.isOpen && !this.results.classList.contains('show-results')) {
      this.resultsContainer.innerHTML = '';
      this.input.setAttribute('aria-expanded', 'false');
      this.setLoadingState(false);
      return;
    }

    this.isOpen = false;
    this.results.classList.remove('show-results');
    siteOverlay.prototype.hideOverlay();
    this.resultsContainer.innerHTML = '';
    this.input.setAttribute('aria-expanded', 'false');
    this.setLoadingState(false);

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  setLoadingState(isLoading) {
    if (isLoading) {
      this.results.setAttribute('loading', '');
    } else {
      this.results.removeAttribute('loading');
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);
